import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { discussComments, feedPosts, hotTrends, siteName } from "@/mocks/guawa";
import useSeo from "@/hooks/useSeo";

interface LocalComment {
  id: string;
  user: string;
  time: string;
  text: string;
  likes: number;
}

const toc = [
  "一、为什么越「土」越上头",
  "二、爽点背后的内容经济学",
  "三、流量之外，留下了什么",
  "四、下半场拼的是产品力",
];

export default function TopicDetail() {
  useSeo({
    title: "短剧霸屏现象讨论现场 - 今日曝瓜 | 围观现场",
    description:
      "围观今日曝瓜本期讨论现场：短剧为何能霸屏下饭时间？从爽点经济学到内容产品力，和瓜友一起理性围观这场现象级热议。",
  });

  const [comments, setComments] = useState<LocalComment[]>(discussComments);
  const [draft, setDraft] = useState("");
  const [liked, setLiked] = useState(false);
  const [posted, setPosted] = useState(false);

  const addComment = () => {
    if (!draft.trim() || posted) return;
    const c: LocalComment = {
      id: `local-${Date.now()}`,
      user: "路过的一颗瓜",
      time: "刚刚",
      text: draft.trim(),
      likes: 0,
    };
    setComments((prev) => [c, ...prev]);
    setDraft("");
    setPosted(true);
  };

  const related = feedPosts.slice(1, 4);
  const sidebarHot = hotTrends.slice(0, 5);

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 md:px-6">
        {/* 面包屑 */}
        <nav className="flex items-center gap-1.5 text-xs text-ink-400" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/topics" className="hover:text-primary-600 whitespace-nowrap">热议专区</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="truncate font-medium text-ink-600">本期讨论现场</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
          {/* 文章主体 */}
          <article id="site-detail" className="rounded-2xl border border-cream-200 bg-white p-5 md:p-8">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-700">爆款热议</span>
                <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">影视剧综</span>
                <span className="text-xs text-ink-400">编辑发布 · 3 小时前</span>
              </div>
              <h1 className="mt-4 text-2xl font-extrabold leading-tight tracking-tight text-ink-900 md:text-3xl">
                短剧为何能霸屏追剧人的下饭时间？一场关于「爽点」的全民围观
              </h1>
              <p className="mt-3 text-base leading-relaxed text-ink-500">
                从通勤路上刷三集，到茶余饭后的谈资，现象级短剧正在重塑人们的时间表。本期我们来围观这场关于「上头」与「吐槽」并存的讨论现场。
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3 border-y border-cream-100 py-3 text-xs text-ink-400">
                <span className="flex items-center gap-1.5"><i className="ri-eye-line text-primary-500"></i> 128.6 万围观</span>
                <span className="flex items-center gap-1.5"><i className="ri-chat-3-line text-accent-500"></i> {comments.length.toLocaleString()} 条讨论</span>
                <button
                  type="button"
                  onClick={() => setLiked((v) => !v)}
                  className={`ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-semibold transition-colors whitespace-nowrap ${
                    liked ? "bg-primary-500 text-white" : "bg-primary-50 text-primary-700 hover:bg-primary-100"
                  }`}
                >
                  <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i> 有用 {486 + (liked ? 1 : 0)}
                </button>
              </div>
            </header>

            <div className="mt-6 text-[15px] leading-8 text-ink-700">
              <h2 className="mt-2 text-lg font-extrabold text-ink-900">{toc[0]}</h2>
              <p className="mt-2">
                很多人一边嫌弃短剧「剧情狗血、制作粗糙」，一边又忍不住一口气刷完。这种矛盾感恰恰说明：它精准击中了人们在碎片时间里对"即时满足"的需求——每一集都有钩子，每一集都能得到回应。
              </p>
              <h2 className="mt-8 text-lg font-extrabold text-ink-900">{toc[1]}</h2>
              <p className="mt-2">
                拆开看，短剧走红背后是一套成熟的内容经济学：前几集免费「喂」甜头，关键转折处设卡，再用密集的反转维持节奏。创作者把长视频"注水"的部分去掉，只留下最抓人的骨架，自然容易让人追下去。
              </p>
              <p className="mt-3">
                评论区里，有人认为这是内容形态的必然进化，也有人担心"爽点轰炸"会让耐心越发稀缺。观点的碰撞，正是这场围观最有意思的地方。
              </p>
              <h2 className="mt-8 text-lg font-extrabold text-ink-900">{toc[2]}</h2>
              <p className="mt-2">
                当流量退潮，真正能留下来的，仍是那些愿意把"套路"升级为"内容"的作品。观众不是不爱看故事，而是厌倦了敷衍的流水线——这一轮讨论，或许能倒逼行业想清楚"热"之后该做什么。
              </p>
              <h2 className="mt-8 text-lg font-extrabold text-ink-900">{toc[3]}</h2>
              <p className="mt-2">
                对普通观众而言，与其被热搜牵着走，不如学会分辨什么是"营销制造的瓜"，什么是真正值得留意的内容趋势。理性的围观，本身就是一种能力。
              </p>
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-cream-100 pt-4 text-xs text-ink-400">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-price-tag-3-line text-primary-500"></i> 短剧 · 内容消费 · 现象热议
              </span>
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap">分享：</span>
                {["ri-weibo-line", "ri-wechat-2-line", "ri-link-m"].map((ic) => (
                  <button
                    key={ic}
                    type="button"
                    aria-label="分享"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-cream-100 text-ink-500 transition-colors hover:bg-primary-100 hover:text-primary-600"
                  >
                    <i className={ic}></i>
                  </button>
                ))}
              </div>
            </div>
          </article>

          {/* 右侧栏 */}
          <aside className="space-y-5">
            <div className="rounded-2xl border border-cream-200 bg-white p-5">
              <h2 className="flex items-center gap-2 text-sm font-bold text-ink-900">
                <i className="ri-list-check-3 text-primary-500"></i> 本期目录
              </h2>
              <ul className="mt-3 flex flex-col gap-2 text-sm">
                {toc.map((t) => (
                  <li key={t}>
                    <a href="#site-detail" className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-ink-600 transition-colors hover:bg-primary-50 hover:text-primary-700">
                      <i className="ri-corner-right-down-line mt-1 text-primary-300"></i> {t}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-cream-200 bg-white p-5">
              <h2 className="flex items-center gap-2 text-sm font-bold text-ink-900">
                <i className="ri-fire-line text-primary-500"></i> 更多围观现场
              </h2>
              <ul className="mt-3 flex flex-col gap-3">
                {sidebarHot.map((t) => (
                  <li key={t.rank}>
                    <Link to="/topic/discuss-2026" className="group flex items-center gap-3">
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white ${t.rank <= 3 ? "bg-primary-500" : "bg-cream-300 text-ink-600"}`}>
                        {t.rank}
                      </span>
                      <span className="line-clamp-2 flex-1 text-sm text-ink-700 transition-colors group-hover:text-primary-700">
                        {t.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* 评论区 */}
        <section className="mt-6 rounded-2xl border border-cream-200 bg-white p-5 md:p-8">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-ink-900">
              <i className="ri-chat-3-fill text-primary-500"></i> 瓜友评论区
              <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-bold text-primary-700">{comments.length}</span>
            </h2>
            <span className="text-xs text-ink-400">理性讨论 · 友善表达</span>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-lg">
              🍉
            </div>
            <form
              className="flex-1"
              onSubmit={(e) => {
                e.preventDefault();
                addComment();
              }}
            >
              <textarea
                value={draft}
                maxLength={200}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setPosted(false);
                }}
                placeholder="说说你的看法，和瓜友一起围观……"
                className="min-h-[88px] w-full resize-none rounded-xl border border-cream-200 bg-cream-50 p-4 text-sm text-ink-800 outline-none transition-colors focus:border-primary-300"
              ></textarea>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-ink-400">{draft.length}/200</span>
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
                >
                  <i className="ri-send-plane-line"></i> 发布围观
                </button>
              </div>
            </form>
          </div>

          {posted && (
            <p className="mt-3 rounded-xl bg-accent-50 px-4 py-2.5 text-sm text-accent-700">
              已加入围观现场，感谢你理性又有料的发言 🎉
            </p>
          )}

          <ul className="mt-6 flex flex-col gap-4">
            {comments.map((c) => (
              <li key={c.id} className="flex gap-3 border-b border-cream-100 pb-4 last:border-0 last:pb-0">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream-100 text-xl">
                  {c.avatar ?? "🍉"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-bold text-ink-800">{c.user}</span>
                    <span className="rounded-full bg-cream-100 px-2 py-0.5 text-[11px] text-ink-500">今日曝瓜用户</span>
                    <span className="text-xs text-ink-400">{c.time}</span>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-700">{c.text}</p>
                  <div className="mt-2 flex items-center gap-4 text-xs text-ink-400">
                    <span className="inline-flex items-center gap-1"><i className="ri-thumb-up-line text-primary-500"></i> {c.likes.toLocaleString()}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-corner-down-left-line text-accent-500"></i> 回复 ({c.replies})</span>
                    <button type="button" className="hover:text-primary-600 whitespace-nowrap">展开回复</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* 相关推荐 */}
        <section className="mt-8">
          <h2 className="text-lg font-extrabold text-ink-900">看完了？再去逛逛这些</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.id}
                to="/topics"
                className="group rounded-2xl border border-cream-200 bg-white p-5 transition-all hover:-translate-y-1 hover:border-primary-200 hover:shadow-soft"
              >
                <span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">{p.cat}</span>
                <h3 className="mt-3 text-sm font-bold leading-snug text-ink-900 transition-colors group-hover:text-primary-700">{p.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-ink-400 line-clamp-2">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      </main>

      {/* BlogPosting 结构化数据 */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: "短剧为何能霸屏追剧人的下饭时间？一场关于爽点的全民围观",
            description: "今日曝瓜本期讨论现场，围观短剧现象级热议。",
            author: { "@type": "Organization", name: siteName },
            publisher: { "@type": "Organization", name: siteName },
            inLanguage: "zh-CN",
            datePublished: "2026-09-06T10:00:00+08:00",
            dateModified: "2026-09-06T10:00:00+08:00",
          }),
        }}
      />

      <Footer />
      <BackToTop />
    </div>
  );
}